import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { log } from "@/lib/log";
import { TrendingUp, AlertTriangle, Zap, DollarSign } from "lucide-react";

/**
 * Payroll Predictor card — visible only for admins on Professional plans
 * whose company has predictor_enabled=true. Fetches /api/payroll/predictor;
 * silently self-hides on 402 (non-pro) or when the toggle is off.
 */
export default function PayrollPredictorCard() {
  const [data, setData] = useState(null);
  const [available, setAvailable] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/predictor");
      if (data?.enabled === false) { setAvailable(false); return; }
      setData(data);
      setAvailable(true);
    } catch (err) {
      // 402 = not on Professional plan → gracefully hide the card.
      if (err?.response?.status === 402) setAvailable(false);
      else log.warn("Predictor fetch failed:", err?.message || err);
    }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);  // refresh every minute
    return () => clearInterval(iv);
  }, [load]);

  if (!available || !data) return null;
  const { totals, budget, employees, progress } = data;
  const pct = Math.round((progress || 0) * 100);
  const util = Math.round((budget.utilization || 0) * 100);
  const hasBudgetAlert = budget.alert;
  const hasOtAlert = budget.ot_alert;

  return (
    <div className="hard-card p-6 mb-8 relative" data-testid="predictor-card">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> § PAYROLL PREDICTOR · PROFESSIONAL</div>
          <div className="font-heading font-black text-2xl tracking-tight">This Week's Projection</div>
          <div className="font-mono text-xs text-muted-foreground mt-1">Week of {data.week_start} → {data.week_end} · {pct}% elapsed · updates every 60s</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {hasOtAlert && <AlertBadge testid="predictor-ot-alert" tone="warn" icon={Zap}>OT projected · {totals.ot_hours.toFixed(1)}h</AlertBadge>}
          {hasBudgetAlert && <AlertBadge testid="predictor-budget-alert" tone="bad" icon={AlertTriangle}>Over budget by {fmtMoney(totals.projected_cost - budget.weekly)}</AlertBadge>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Metric label="Actual so far" value={fmtMoney(totals.actual_cost)} sub={`${totals.actual_hours.toFixed(1)}h clocked`} testid="predictor-actual" />
        <Metric label="Projected week-end" value={fmtMoney(totals.projected_cost)} sub={`${totals.projected_hours.toFixed(1)}h expected`} color={hasBudgetAlert ? "text-bad" : "text-good"} testid="predictor-projected" />
        <Metric label="OT · projected" value={`${totals.ot_hours.toFixed(1)}h`} sub={`${fmtMoney(totals.ot_cost)} at 1.5×`} color={hasOtAlert ? "text-warn" : ""} testid="predictor-ot" />
        <Metric label="Weekly Budget" value={budget.weekly > 0 ? fmtMoney(budget.weekly) : "—"} sub={budget.weekly > 0 ? `${util}% utilized` : "Set a budget in Company"} color={util > 100 ? "text-bad" : ""} testid="predictor-budget" />
      </div>

      {budget.weekly > 0 && (
        <div className="mb-4">
          <div className="h-3 border-2 border-ink relative overflow-hidden bg-white">
            <div
              className={`h-full ${util > 100 ? "bg-bad" : util > 80 ? "bg-warn" : "bg-good"}`}
              style={{ width: `${Math.min(100, util)}%` }}
              data-testid="predictor-progress-bar"
            />
          </div>
          <div className="font-mono text-[10px] text-muted-foreground mt-1">Projection vs. budget — {util}% ({fmtMoney(totals.projected_cost)} of {fmtMoney(budget.weekly)})</div>
        </div>
      )}

      {employees.length > 0 && (
        <details className="mt-2">
          <summary className="font-mono text-xs uppercase tracking-widest cursor-pointer hover:text-good" data-testid="predictor-details-toggle">Per-employee breakdown ({employees.length})</summary>
          <table className="w-full font-mono text-xs mt-3" data-testid="predictor-employees-table">
            <thead className="bg-ink text-white">
              <tr>
                <th className="text-left px-2 py-2 uppercase text-[10px]">Employee</th>
                <th className="text-right px-2 py-2 uppercase text-[10px]">Rate</th>
                <th className="text-right px-2 py-2 uppercase text-[10px]">Actual Hrs</th>
                <th className="text-right px-2 py-2 uppercase text-[10px]">Projected Hrs</th>
                <th className="text-right px-2 py-2 uppercase text-[10px]">OT Hrs</th>
                <th className="text-right px-2 py-2 uppercase text-[10px]">Projected $</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e, i) => (
                <tr key={e.employee_id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`predictor-row-${e.employee_id}`}>
                  <td className="px-2 py-1.5 border-t border-ink/20">{e.employee_name}</td>
                  <td className="px-2 py-1.5 border-t border-ink/20 text-right">{fmtMoney(e.hourly_rate)}/h</td>
                  <td className="px-2 py-1.5 border-t border-ink/20 text-right tabular-nums">{e.actual_hours.toFixed(2)}</td>
                  <td className="px-2 py-1.5 border-t border-ink/20 text-right tabular-nums">{e.projected_hours.toFixed(2)}</td>
                  <td className={`px-2 py-1.5 border-t border-ink/20 text-right tabular-nums ${e.ot_hours > 0 ? "text-warn font-black" : ""}`}>{e.ot_hours.toFixed(2)}</td>
                  <td className="px-2 py-1.5 border-t border-ink/20 text-right tabular-nums font-black">{fmtMoney(e.projected_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <Link to="/app/company" className="mt-3 inline-block font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-ink underline" data-testid="predictor-settings-link">Turn off / edit budget in Company settings →</Link>
    </div>
  );
}

function Metric({ label, value, sub, color = "", testid }) {
  return (
    <div className="border-2 border-ink p-3 bg-white">
      <div className="overline text-[9px]">{label}</div>
      <div className={`font-mono font-black text-lg tabular-nums ${color}`} data-testid={testid}>{value}</div>
      <div className="font-mono text-[9px] text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

function AlertBadge({ children, tone, icon: Icon, testid }) {
  const cls = tone === "bad" ? "bg-bad text-white" : "bg-warn text-ink";
  return <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${cls}`} data-testid={testid}><Icon className="w-3 h-3" />{children}</span>;
}
